<script setup lang="ts">
import { EditorContent, useEditor } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import { Extension, Node } from '@tiptap/core'
import { Plugin } from '@tiptap/pm/state'
import EmailVariableCommandList from '@/views/email/components/EmailVariableCommandList.vue'
import type { EmailVariable } from '@/views/email/constants/variables'
import { ConsentVariable } from './ConsentVariableExtension'
import { createConsentVariableSuggestion, type ConsentVariableSuggestionState } from './consentVariableSuggestion'
import {
  CONSENT_VARIABLE_NAMES,
  parseWordingToDoc,
  renderWordingWithLabels,
  serializeDocToWording,
  type ConsentVariableName
} from './consentWording'

const props = defineProps<{
  id?: string
  modelValue: string
  maxLength: number
  hasCguUrl: boolean
  hasPrivacyPolicyUrl: boolean
  defaultWording?: string
}>()

const emit = defineEmits<(e: 'update:modelValue', value: string) => void>()

const { t } = useI18n()

const renderedLinkLabels = computed<Record<ConsentVariableName, string>>(() => ({
  cgu: t('pages.loyaltySupports.webapp.config.cguLinkLabel'),
  privacyPolicy: t('pages.loyaltySupports.webapp.config.privacyPolicyLinkLabel')
}))

const renderedLength = (wording: string) => renderWordingWithLabels(wording, renderedLinkLabels.value).length

// Describes what each variable points to (matches the URL field labels above) — purely
// for the insert picker. It must never be used as the inserted/rendered wording itself.
const variablePickerLabels = computed<Record<ConsentVariableName, string>>(() => ({
  cgu: t('pages.loyaltySupports.webapp.config.cguUrl'),
  privacyPolicy: t('pages.loyaltySupports.webapp.config.privacyPolicyUrl')
}))

const consentVariables = computed<EmailVariable[]>(() =>
  CONSENT_VARIABLE_NAMES.map((name) => ({
    key: name,
    label: variablePickerLabels.value[name],
    description: '',
    example: ''
  }))
)

const isVariableAvailable = (name: string) => (name === 'cgu' ? props.hasCguUrl : props.hasPrivacyPolicyUrl)

const countVariable = (wording: string, name: string) => {
  const pattern = new RegExp(`\\{${name}(?::[^}]+)?\\}`, 'g')
  return (wording.match(pattern) ?? []).length
}

const isVariableUsed = (name: string) => countVariable(currentDocWording.value, name) > 0

const availableVariables = computed<EmailVariable[]>(() =>
  consentVariables.value.filter((variable) => isVariableAvailable(variable.key) && !isVariableUsed(variable.key))
)

const suggestion = reactive({
  open: false,
  items: [] as EmailVariable[],
  activeIndex: 0,
  top: 0,
  left: 0,
  apply: null as ((variable: EmailVariable) => void) | null
})

const variableSuggestion = createConsentVariableSuggestion(() => availableVariables.value, {
  isOpen: () => suggestion.open,
  onStart: (state) => openSuggestion(state),
  onUpdate: (state) => openSuggestion(state),
  onExit: () => {
    suggestion.open = false
  },
  onArrowDown: () => moveSuggestion(1),
  onArrowUp: () => moveSuggestion(-1),
  onEnter: () => {
    const variable = suggestion.items[suggestion.activeIndex]
    if (variable) applySuggestion(variable)
    return true
  }
})

const OneParagraphDocument = Node.create({
  name: 'doc',
  topNode: true,
  content: 'paragraph'
})

const wordingLimit = Extension.create({
  name: 'consentWordingLimit',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        filterTransaction: (transaction, state) => {
          if (!transaction.docChanged) return true
          const nextWording = serializeDocToWording(transaction.doc.toJSON())
          const previousWording = serializeDocToWording(state.doc.toJSON())
          const exceeds = (wording: string) =>
            renderedLength(wording) > props.maxLength || wording.length > props.maxLength
          if (exceeds(nextWording) && !exceeds(previousWording)) return false
          return !CONSENT_VARIABLE_NAMES.some(
            (name) =>
              countVariable(nextWording, name) > 1 &&
              countVariable(nextWording, name) > countVariable(previousWording, name)
          )
        }
      })
    ]
  }
})

const lastEmittedValue = ref(props.modelValue)
const displayedWording = () => props.modelValue || props.defaultWording || ''
const currentDocWording = ref(displayedWording())

const editor = useEditor({
  content: parseWordingToDoc(displayedWording()),
  extensions: [
    OneParagraphDocument,
    StarterKit.configure({
      document: false,
      blockquote: false,
      bold: false,
      bulletList: false,
      code: false,
      codeBlock: false,
      dropcursor: false,
      gapcursor: false,
      hardBreak: false,
      heading: false,
      horizontalRule: false,
      italic: false,
      link: false,
      listItem: false,
      listKeymap: false,
      orderedList: false,
      strike: false,
      underline: false,
      trailingNode: false
    }),
    ConsentVariable.configure({
      getLabel: (name) => renderedLinkLabels.value[name as ConsentVariableName] ?? name
    }),
    variableSuggestion,
    wordingLimit,
    Placeholder.configure({
      placeholder: () => t('pages.loyaltySupports.webapp.config.cguWordingPlaceholder')
    })
  ],
  editorProps: {
    attributes: {
      class: 'focus:outline-none',
      ...(props.id ? { id: props.id } : {})
    }
  },
  onUpdate: ({ editor }) => {
    const wording = serializeDocToWording(editor.getJSON())
    currentDocWording.value = wording
    lastEmittedValue.value = wording
    emit('update:modelValue', wording)
  },
  onBlur: () => {
    suggestion.open = false
  }
})

const applyDisplayedWording = () => {
  const wording = displayedWording()
  currentDocWording.value = wording
  editor.value?.commands.setContent(parseWordingToDoc(wording), { emitUpdate: false })
}

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue === lastEmittedValue.value) return
    lastEmittedValue.value = newValue
    applyDisplayedWording()
  }
)

watch(
  () => props.defaultWording,
  () => {
    if (props.modelValue?.trim()) return
    applyDisplayedWording()
  }
)

const showVariablePicker = ref(false)

const getSelectedText = (): string | undefined => {
  const state = editor.value?.state
  if (!state || state.selection.empty) return undefined
  const { from, to } = state.selection
  // `}` would break the `{name:label}` serialization format
  return state.doc.textBetween(from, to, ' ').replace(/}/g, '').trim() || undefined
}

const insertVariable = (name: string) => {
  editor.value?.chain().focus().insertConsentVariable(name, getSelectedText()).run()
  showVariablePicker.value = false
}

function openSuggestion(state: ConsentVariableSuggestionState) {
  suggestion.items = state.items
  suggestion.apply = state.command
  if (state.rect) {
    suggestion.top = state.rect.bottom + 4
    suggestion.left = state.rect.left
  }
  if (suggestion.activeIndex >= state.items.length) suggestion.activeIndex = 0
  suggestion.open = state.items.length > 0
}

function moveSuggestion(delta: number): boolean {
  const count = suggestion.items.length
  if (count === 0) return false
  suggestion.activeIndex = (suggestion.activeIndex + delta + count) % count
  return true
}

function applySuggestion(variable: EmailVariable) {
  suggestion.apply?.(variable)
  suggestion.open = false
}

onBeforeUnmount(() => {
  editor.value?.destroy()
})
</script>

<template>
  <div
    class="border-input focus-within:border-ring focus-within:ring-ring/50 flex w-full flex-col rounded-md border bg-transparent text-base shadow-xs transition-[color,box-shadow] focus-within:ring-[3px] md:text-sm"
  >
    <div class="flex items-center gap-0.5 rounded-t-md border-b border-border bg-white px-1 py-1">
      <div class="relative flex items-center gap-px">
        <button
          type="button"
          class="flex min-h-8 items-center justify-center rounded px-2 py-1.5 text-muted-foreground transition-all hover:bg-accent hover:text-foreground"
          :class="{ 'bg-accent text-foreground': showVariablePicker }"
          :title="t('pages.loyaltySupports.webapp.config.cguWordingInsert')"
          @click="showVariablePicker = !showVariablePicker"
        >
          <BaseIcon name="IconoirCodeBrackets" class="h-5 w-5" />
        </button>
        <div
          v-if="showVariablePicker"
          class="absolute left-0 top-full z-[60] mt-1 min-w-44 rounded-lg border border-border bg-white p-1 shadow-lg"
        >
          <button
            v-for="variable in consentVariables"
            :key="variable.key"
            type="button"
            :disabled="!isVariableAvailable(variable.key) || isVariableUsed(variable.key)"
            class="flex w-full items-center rounded-md px-3 py-2 text-left text-sm transition-colors"
            :class="
              isVariableAvailable(variable.key) && !isVariableUsed(variable.key)
                ? 'text-foreground hover:bg-accent'
                : 'cursor-not-allowed text-foreground-disabled'
            "
            :title="
              !isVariableAvailable(variable.key)
                ? t('pages.loyaltySupports.webapp.config.cguWordingVariableDisabled')
                : isVariableUsed(variable.key)
                  ? t('pages.loyaltySupports.webapp.config.cguWordingVariableUsed')
                  : undefined
            "
            @click="insertVariable(variable.key)"
          >
            {{ variable.label }}
          </button>
        </div>
      </div>
    </div>
    <EditorContent :editor="editor" class="consent-wording-editor min-h-16 w-full px-3 py-2" />
  </div>

  <div v-if="showVariablePicker" class="fixed inset-0 z-40" @click="showVariablePicker = false" />

  <Teleport to="body">
    <div
      v-if="suggestion.open"
      class="fixed z-[100]"
      :style="{ top: `${suggestion.top}px`, left: `${suggestion.left}px` }"
    >
      <EmailVariableCommandList
        :items="suggestion.items"
        :active-index="suggestion.activeIndex"
        @select="applySuggestion"
        @hover="suggestion.activeIndex = $event"
      />
    </div>
  </Teleport>
</template>

<style scoped>
@reference "@/input.css";

.consent-wording-editor :deep(.ProseMirror) {
  @apply outline-none min-h-6;
}
.consent-wording-editor :deep(p) {
  @apply m-0;
}
.consent-wording-editor :deep(p.is-editor-empty:first-child::before) {
  @apply text-muted-foreground italic float-left h-0 pointer-events-none;
  content: attr(data-placeholder);
}
</style>
